import type { CancelTokenSource } from 'axios'
import axios from 'axios'
import DataLoader from 'dataloader'

import configs from '@acre/config'

import { clientLoader } from '../loaders/client'
import { mortgageLoader } from '../loaders/mortgage'
import cpRequest from '../requesters/clientPortal'
import request from '../requesters/default'
import oathKeeper from '../requesters/oathKeeper'
import type {
  AcreProcessorUploadSignedCopyBody,
  CdmAddCaseDocumentResponse,
  CdmCreateDocumentVerificationResponse,
  CdmDeleteCaseDocumentResponse,
  CdmDocument,
  CdmGetDocumentRequirementsResponse,
  CdmGetDocumentResponse,
  CdmGetDocumentSummariesResponse,
  CdmGetDocumentVerificationResponse,
  CdmRenderTemplateRequest,
  CdmRenderTemplateResponse,
  CdmReplaceDocumentResponse,
  CdmTestRenderTemplateRequest,
  CdmTestRenderTemplateResponse,
  CdmUpdateDocumentResponse,
  CdmUploadDocumentRequest,
  CdmUploadDocumentResponse,
  CdmUploadSignedCopyResponse,
  SrvAcreProcessorAddCaseDocumentBody,
  SrvAcreProcessorCreateDocumentVerificationBody,
} from '../service/luther/model'
import { setVerificationCacheInvalidation } from '../utils/caseVerificationInvalidation'
import { formatInferenceResultType, formatMIDocument } from '../utils/schemaMapping/document'
import { CaseLoader, CasesByClientLoader } from './case'

const { API1_URL } = configs

export const DocumentDetailsLoader = new DataLoader(
  (
    payloads: {
      documentId: string
      params?: QueryDocumentDetailsArgs['params']
    }[],
  ) => Promise.all(payloads.map((payload) => getDocumentBatchFn(payload.documentId, payload.params))),
)

// GetDocument
// https://api.acre.software/api_documentation/redoc.html#operation/GetDocument
const getDocumentBatchFn = async (documentId: string, params?: QueryDocumentDetailsArgs['params']) => {
  const response = await request.get<CdmGetDocumentResponse>(`/document/${documentId}`, {
    params,
  })
  return response?.data
}

export const getDocument = async (documentId: string, params?: QueryDocumentDetailsArgs['params']) => {
  return DocumentDetailsLoader.load({ documentId, params })
}

// GetMiDocumentSummaries
export const getMiDocumentSummaries = async (
  entitity: 'case' | 'client',
  id: string,
  filters?: Maybe<MiDocumentSummaryFiltersInput>,
  fromClientPortal?: boolean | null,
) => {
  if (fromClientPortal) {
    const useOathKeeper = sessionStorage.getItem('useOathKeeper')
    const requesterCP = useOathKeeper === 'true' ? oathKeeper : cpRequest

    const response = await requesterCP.get<MiDocumentSummaryResponse>(`/mi/${entitity}/${id}/documents`, {
      params: { ...filters, page_size: filters?.page_size ?? 500 },
    })

    return {
      ...response.data,
      documents: response.data?.documents ? response.data?.documents?.map(formatMIDocument) : null,
    }
  } else {
    const response = await request.get<MiDocumentSummaryResponse>(`/mi/${entitity}/${id}/documents`, {
      baseURL: `${API1_URL}/acre`,
      params: { ...filters, page_size: filters?.page_size ?? 500 },
    })

    return {
      ...response.data,
      documents: response.data?.documents ? response.data?.documents?.map(formatMIDocument) : null,
    }
  }
}

// Fetches MI document summaries for a case and clients and merge the results (deduped by document_id).
// Case documents are processed first so that, when the same document_id appears in
// both the case and a client, the case's copy wins and is positioned ahead of the
// client copies.
export const getCaseAndClientsMIDocumentsSummaries = async (caseId: string, clientIds: string[]) => {
  const [caseResponse, ...clientResponses] = await Promise.all([
    getMiDocumentSummaries('case', caseId, undefined, false),
    ...clientIds.map((clientId) => getMiDocumentSummaries('client', clientId, undefined, false)),
  ])

  const seenDocumentIds = new Set<string>()
  const mergedDocuments: NonNullable<MiDocumentSummaryResponse['documents']> = []

  for (const miDocumentResponse of [caseResponse, ...clientResponses]) {
    for (const document of miDocumentResponse.documents ?? []) {
      const documentId = document.document_id
      if (documentId && seenDocumentIds.has(documentId)) {
        continue
      }
      if (documentId) {
        seenDocumentIds.add(documentId)
      }
      mergedDocuments.push(document)
    }
  }

  return { documents: mergedDocuments, filters: null, next_page: null }
}

export const getMiDocumentTotals = async (
  entitity: 'case' | 'client',
  id: string,
  filters?: Maybe<MiDocumentTotalsFiltersInput>,
) => {
  const response = await request.get<MiDocumentTotalsResponse>(`/mi/${entitity}/${id}/documents/totals`, {
    baseURL: `${API1_URL}/acre`,
    params: filters,
  })

  return response?.data?.Totals
}

// UploadDocument
// https://api.acre.software/api_documentation/redoc.html#operation/UploadDocument
export const uploadDocument = async (body: CdmUploadDocumentRequest) => {
  const response = await request.post<CdmUploadDocumentResponse>('/document', body)

  // If mortgage id is present, clear the cached mortgages from dataloader,
  // otherwise upon mortgage refetch in GQL, eor_documents will not post latest results
  if (body.mortgage_id) {
    mortgageLoader.clearAll()
  }

  // Set BE verification cache invalidation flag
  setVerificationCacheInvalidation()

  return response?.data
}

// RenderTemplate
// https://api.acre.software/api_documentation/redoc.html#operation/RenderTemplate
export const renderTemplate = async (body: CdmRenderTemplateRequest) => {
  const response = await request.post<CdmRenderTemplateResponse>('/document/render_template', body)

  CaseLoader.clearAll()
  CasesByClientLoader.clearAll()
  clientLoader.clearAll()

  // Set BE verification cache invalidation flag
  setVerificationCacheInvalidation()

  return response?.data
}

export const renderTestTemplate = async (body: CdmTestRenderTemplateRequest) => {
  const response = await request.post<CdmTestRenderTemplateResponse>('/document/render_template/test', body)

  return response?.data
}

// UpdateDocument
// https://api.acre.software/api_documentation/redoc.html#operation/UpdateDocument
export const updateDocument = async (documentId: string, body: CdmDocument) => {
  const response = await request.patch<CdmUpdateDocumentResponse>(`/document/${documentId}`, body)

  // Set BE verification cache invalidation flag
  setVerificationCacheInvalidation()

  return response?.data
}

// ReplaceDocument
// https://api.acre.software/api_documentation/redoc.html#operation/ReplaceDocument
export const replaceDocument = async (documentId: string, body: CdmUploadDocumentRequest) => {
  const response = await request.put<CdmReplaceDocumentResponse>(`/document/${documentId}`, body)

  // Set BE verification cache invalidation flag
  setVerificationCacheInvalidation()

  return response?.data
}

// GetDocumentVerification
// https://api.acre.software/api_documentation/redoc.html#operation/GetDocumentVerification
export const getDocumentVerification = async (documentId: string, params?: Maybe<DocumentVerificationQueryParams>) => {
  const response = await request.get<CdmGetDocumentVerificationResponse>(`/document/${documentId}/verification`, {
    params,
  })
  return response?.data
}

// GetDocumentRequirements
// https://api.acre.software/api_documentation/redoc.html#operation/GetDocumentRequirements
export const getDocumentRequirements = async (filterOrganisationIds: string[], requirementDetails: Maybe<boolean>) => {
  const response = await request.get<CdmGetDocumentRequirementsResponse>(
    `/requirement?filter_organisation_ids=${filterOrganisationIds}&requirement_details=${requirementDetails ?? true}`,
  )
  return response?.data
}

// CreateDocumentVerification
// https://api.acre.software/api_documentation/redoc.html#operation/CreateDocumentVerification
export const createDocumentVerification = async (
  documentId: string,
  body: SrvAcreProcessorCreateDocumentVerificationBody,
) => {
  const response = await request.post<CdmCreateDocumentVerificationResponse>(
    `/document/${documentId}/verification`,
    body,
  )

  // Set BE verification cache invalidation flag
  setVerificationCacheInvalidation()

  return response?.data
}

// AddCaseDocument
// https://api.acre.software/api_documentation/redoc.html#operation/AddCaseDocument
export const addCaseDocument = async (documentId: string, body: SrvAcreProcessorAddCaseDocumentBody) => {
  const response = await request.post<CdmAddCaseDocumentResponse>(`/document/${documentId}/case`, body)

  // Set BE verification cache invalidation flag
  setVerificationCacheInvalidation()

  return response?.data
}

// DeleteCaseDocument
// https://api.acre.software/api_documentation/redoc.html#operation/DeleteCaseDocument
export const deleteCaseDocument = async (documentId: string, caseId: string) => {
  const response = await request.delete<CdmDeleteCaseDocumentResponse>(`/document/${documentId}/case/${caseId}`)

  // Set BE verification cache invalidation flag
  setVerificationCacheInvalidation()

  return response?.data
}

// UploadSignedCopy
// POST /document/{documentId}/signed_copy
export const uploadSignedCopy = async (documentId: string, body: AcreProcessorUploadSignedCopyBody) => {
  const response = await request.post<CdmUploadSignedCopyResponse>(`/document/${documentId}/signed_copy`, body, {
    baseURL: API1_URL,
  })

  setVerificationCacheInvalidation()

  return response?.data
}

// ClassifyDocumentWithMistral
// POST /acre/classification/annotations
export const classifyDocumentWithMistral = async (
  body: MistralInput,
  signal?: AbortSignal,
): Promise<MistralResponse | undefined> => {
  let cancelTokenSource: CancelTokenSource | undefined
  if (signal) {
    cancelTokenSource = axios.CancelToken.source()
    signal.addEventListener('abort', () => {
      cancelTokenSource?.cancel('Request cancelled by user')
    })
  }

  try {
    const response = await request.post<MistralResponse>('/classification/annotations?model=medium', body, {
      cancelToken: cancelTokenSource?.token,
    })
    return response?.data
  } catch (error) {
    if (axios.isCancel(error)) {
      return undefined
    }
    throw error
  }
}

// UploadDocumentClassification
// POST /acre/classification/upload_document
export const uploadDocumentClassification = async (
  body: ClassificationUploadDocumentInput,
): Promise<ClassificationUploadDocumentResponse | undefined> => {
  const response = await request.post<ClassificationUploadDocumentResponse>('/classification/upload_document', body)
  return {
    ...response?.data,
    document: {
      ...response?.data?.document,
      inferenceResult: {
        ...response?.data?.document?.inferenceResult,
        type: formatInferenceResultType(response?.data?.document?.inferenceResult?.type),
      },
    },
  }
}

// TODO remove as part ofRD-16248
// GetDocumentSummaries
// https://crm.dev.acreplatforms.co.uk/api_documentation/redoc.html#operation/GetDocumentSummaries
export const getDocumentSummariesPhylum = async (params?: QueryDocumentSummariesArgs) => {
  const response = await request.get<CdmGetDocumentSummariesResponse>('/document', { params })
  return response?.data
}
